import React, { useEffect, useMemo, useRef, useState } from "react";
import { Bot, Camera, Loader2, MapPin, Send, X } from "lucide-react";

const money = (value) => Number.isFinite(Number(value))
  ? Number(value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
  : "valor atualizando";

const greeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Bom dia";
  if (hour < 18) return "Boa tarde";
  return "Boa noite";
};

const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result || ""));
  reader.onerror = () => reject(new Error("Não foi possível ler a foto."));
  reader.readAsDataURL(file);
});

export default function ProducerAIChat({ open, onClose, referencePrice, coffeeLabel }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const fileRef = useRef(null);
  const endRef = useRef(null);
  const referenceText = useMemo(() => money(referencePrice), [referencePrice]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, loading]);
  useEffect(() => {
    if (!open) { setMessages([]); setInput(""); setLocation(null); return; }
    begin();
  }, [open]);

  async function begin() {
    setStarting(true);
    const fallback = `${greeting()}! Sou a assistente do produtor COFY. Hoje a referência para ${coffeeLabel} está em ${referenceText} por saca. Você tem café para vender? Posso começar fazendo uma análise inicial por foto.`;
    if (!navigator.geolocation) { setMessages([{ from: "assistant", text: fallback }]); setStarting(false); return; }
    navigator.geolocation.getCurrentPosition(async ({ coords }) => {
      try {
        const response = await fetch("/api/reverse_geocode.php", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ latitude: coords.latitude, longitude: coords.longitude }) });
        const place = await response.json();
        if (!response.ok) throw new Error(place.error || "Localização indisponível");
        const city = place.city || "sua região";
        const state = place.state || "";
        const loc = { city, state, latitude: coords.latitude, longitude: coords.longitude };
        setLocation(loc);
        setMessages([{ from: "assistant", text: `${greeting()}! Vi que você está em ${city}${state ? `, ${state}` : ""}. Hoje a referência para ${coffeeLabel} está em ${referenceText} por saca. Você é produtor e tem café para vender? Envie uma foto do café e eu começo uma análise inicial.` }]);
      } catch (_) { setMessages([{ from: "assistant", text: fallback }]); }
      finally { setStarting(false); }
    }, () => { setMessages([{ from: "assistant", text: fallback + " Não consegui confirmar sua cidade; você pode escrevê-la durante a conversa." }]); setStarting(false); }, { timeout: 9000, maximumAge: 300000 });
  }

  async function send(text = "", image = null) {
    const clean = text.trim();
    if ((!clean && !image) || loading) return;
    const userMessage = { from: "user", text: clean || "Enviei uma foto do meu café.", image };
    const next = [...messages, userMessage];
    setMessages(next); setInput(""); setLoading(true);
    try {
      const response = await fetch("/api/assistant_chat.php", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ message: clean, image, history: next.slice(-12).map(m => ({ role: m.from === "assistant" ? "assistant" : "user", content: m.text })), context: { location, reference_price_brl: Number(referencePrice) || null, coffee_reference: coffeeLabel } }) });
      const data = await response.json();
      if (!response.ok || data.error) throw new Error(data.error || "Falha na IA");
      setMessages(current => [...current, { from: "assistant", text: data.reply || data.answer || data.message }]);
    } catch (_) {
      setMessages(current => [...current, { from: "assistant", text: "Não consegui responder agora. Tente novamente. A foto é apenas uma pré-análise e não substitui classificação física nem prova de xícara." }]);
    } finally { setLoading(false); }
  }

  async function choosePhoto(event) {
    const file = event.target.files?.[0]; event.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return alert("Escolha uma foto do café.");
    if (file.size > 8 * 1024 * 1024) return alert("A foto deve ter no máximo 8 MB.");
    send("", await fileToDataUrl(file));
  }

  if (!open) return null;
  return <div className="fixed inset-0 z-[100] bg-slate-950 text-white">
    <div className="mx-auto flex h-[100dvh] w-full max-w-3xl flex-col">
      <header className="flex items-center justify-between border-b border-violet-500/30 bg-slate-950 px-4 py-3">
        <div className="flex items-center gap-3"><div className="rounded-2xl bg-violet-500/20 p-3"><Bot className="h-7 w-7 text-violet-200" /></div><div><p className="text-xl font-black">IA do Produtor</p><p className="flex items-center gap-1 text-sm text-slate-300"><MapPin className="h-4 w-4" />{location?.city ? `${location.city}${location.state ? ` - ${location.state}` : ""}` : starting ? "Identificando sua região..." : "COFY Café"}</p></div></div>
        <button onClick={onClose} className="rounded-full border border-slate-700 p-3" aria-label="Fechar"><X className="h-6 w-6" /></button>
      </header>
      <main className="flex-1 space-y-4 overflow-y-auto px-4 py-5">
        {starting && messages.length === 0 && <div className="flex items-center gap-3 text-lg"><Loader2 className="h-6 w-6 animate-spin text-violet-300" />Preparando seu atendimento...</div>}
        {messages.map((m, i) => <div key={i} className={`flex ${m.from === "user" ? "justify-end" : "justify-start"}`}><div className={`max-w-[92%] rounded-3xl px-5 py-4 text-[18px] leading-relaxed shadow-lg ${m.from === "user" ? "rounded-br-md bg-violet-500" : "rounded-bl-md border border-slate-700 bg-slate-900"}`}>{m.image && <img src={m.image} alt="Café enviado" className="mb-3 max-h-64 w-full rounded-2xl object-cover" />}<p className="whitespace-pre-wrap">{m.text}</p></div></div>)}
        {loading && <div className="flex justify-start"><div className="flex items-center gap-3 rounded-3xl border border-slate-700 bg-slate-900 px-5 py-4 text-lg"><Loader2 className="h-5 w-5 animate-spin text-violet-300" />Analisando...</div></div>}
        <div ref={endRef} />
      </main>
      <footer className="border-t border-slate-800 bg-slate-950 p-3 pb-[max(12px,env(safe-area-inset-bottom))]">
        <div className="flex items-end gap-2"><input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={choosePhoto} /><button onClick={() => fileRef.current?.click()} className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-amber-500/50 bg-amber-500/10" aria-label="Foto"><Camera className="h-7 w-7 text-amber-200" /></button><textarea rows={1} value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(input); } }} placeholder="Escreva aqui..." className="min-h-14 max-h-32 flex-1 resize-none rounded-2xl border border-slate-700 bg-slate-900 px-4 py-4 text-lg outline-none focus:border-violet-500" /><button onClick={() => send(input)} disabled={!input.trim() || loading} className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-violet-500 disabled:opacity-40" aria-label="Enviar"><Send className="h-7 w-7" /></button></div>
      </footer>
    </div>
  </div>;
}
